import React, { useContext, useEffect } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { Avatar, Box, Chip, CircularProgress, Divider, Grid, Stack, Tab, Tabs, Typography } from "@mui/material";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import MapOutlinedIcon from "@mui/icons-material/MapOutlined";
import KeyIcon from "@mui/icons-material/VpnKeyOutlined";
import { UserContext } from "../App";
import {
    loadOwnPedalAlert, loadOwnPedalBoard, useGetOwnPedalBoard, useGetProfileUser, useGetProfileUserEnrollments,
    useGetUserDashboard
} from "../../services/UserDataManager";
import { getGenderCodeValue } from "../../constants/genderCode";
import PedalBoard from "../../components/pedal/PedalBoard";
import PinbikerTokens from "./PinbikerTokens";
import { PINBIKER_HEADER_HEIGHT } from "./PinbikerHeader";
import SideRail, { RAIL_WIDTH } from "./SideRail";
import { getBrand } from "../../branding";

// Tinte dello stile della pagina: derivano dai colori del tema (rosso #dd3333, verde #8BC34A).
const RED = "#dd3333";
const RED_TINT = "#fff5f5";
const GREEN = "#5f9f2a";
const GREEN_TINT = "#f3f9ec";
const GREY_TINT = "#fafafb";
const RADIUS = "14px";
// La mappa occupa lo schermo sotto l'header (spazi di MainPinbiker: 24 sopra, 32 sotto).
const MAP_TAB_HEIGHT = { xs: "auto", md: `calc(100vh - ${PINBIKER_HEADER_HEIGHT + 24 + 32}px)` };

/**
 * Pagina del pinbiker che entra dal sito web (solo la shell MainPinbiker: admin e manager usano
 * ProfilePage, che resta com'e'), impaginata come la UGO Web Board: barra scura a sinistra con tre
 * schede — profilo (nome e contatti, totali, iniziative), mappa del pedale con gli allarmi, token di
 * accesso — che su telefono diventano schede in alto. Chi ha un pedale entra sulla mappa, gli altri sul
 * profilo. Mostra sempre l'utente loggato, qualunque uid sia nell'URL.
 */
export default function PinbikerProfile() {
    const { t } = useTranslation();
    const history = useHistory();
    const loggedUser = useContext(UserContext);
    const { filter } = useParams();
    const uid = loggedUser.uid;
    const brand = getBrand();

    // i pedali li elenca il server UGO: stessa query del pannello, nessuna chiamata in piu'
    const { devices: pedals, status: pedalStatus } = useGetOwnPedalBoard();
    const hasPedal = pedals.length > 0;

    const tabs = [
        { filter: "profile", label: t('profileTab'), icon: <PersonOutlineIcon /> },
        { filter: "map", label: t('map'), icon: <MapOutlinedIcon /> },
        { filter: "tokens", label: t('loginTabCode'), icon: <KeyIcon /> },
    ];
    const current = tabs.find(tab => tab.filter === filter);
    const go = value => history.push(`/profile/${uid}/${value}`);

    // senza scheda nell'URL (o con una sconosciuta, es. i vecchi link /sessions) si sceglie qui
    useEffect(() => {
        if (current || pedalStatus === "loading") return;
        history.replace(`/profile/${uid}/${hasPedal ? "map" : "profile"}`);
    }, [current, pedalStatus, hasPedal, history, uid]);

    if (!current) return (
        <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}><CircularProgress /></Box>
    );

    return (
        <>
            <SideRail items={tabs.map(tab => ({ key: tab.filter, label: tab.label, icon: tab.icon }))}
                active={current.filter} accent={brand.accent} onSelect={go} />

            <Box sx={{ ml: { md: `${RAIL_WIDTH}px` } }}>
                <Tabs value={current.filter} onChange={(_, value) => go(value)} variant="fullWidth"
                    sx={{
                        display: { md: "none" }, mb: 2, bgcolor: "#fff", borderRadius: RADIUS,
                        "& .MuiTab-root": { textTransform: "none", fontWeight: 600, minHeight: 56 },
                        "& .Mui-selected": { color: `${brand.accent} !important` },
                        "& .MuiTabs-indicator": { bgcolor: brand.accent },
                    }}>
                    {tabs.map(tab => <Tab key={tab.filter} value={tab.filter} label={tab.label} icon={tab.icon} iconPosition="start" />)}
                </Tabs>

                {current.filter === "profile" && <ProfileTab uid={uid} />}
                {current.filter === "map" &&
                    <PedalBoard queryKey={["me-pedal"]} loadBoard={loadOwnPedalBoard} loadAlert={loadOwnPedalAlert}
                        height={MAP_TAB_HEIGHT}
                        empty={<Panel><Typography color="text.secondary">{t('noPedalPosition')}</Typography></Panel>} />}
                {current.filter === "tokens" && <Panel><PinbikerTokens brand={brand} hasPedal={hasPedal} pedals={pedals} /></Panel>}
            </Box>
        </>
    );
}

function Panel({ children }) {
    return (
        <Box sx={{ bgcolor: "#fff", borderRadius: RADIUS, p: { xs: 2, md: 4 }, maxWidth: 1280, mx: "auto" }}>
            {children}
        </Box>
    );
}

/**
 * Scheda profilo: nome e contatti, totali e iniziative. Niente dati anagrafici sensibili (codice fiscale, IBAN,
 * matricola, indirizzo di casa) e niente sedi di lavoro: su questa pagina non si mostrano.
 */
function ProfileTab({ uid }) {
    const { t } = useTranslation();
    const { user = {} } = useGetProfileUser(uid);
    const { dashboard = {} } = useGetUserDashboard(uid);
    const { enrollments = [] } = useGetProfileUserEnrollments(uid);

    return (
        <Panel>
            <Stack spacing={4}>
                <ProfileCard user={user} />
                <Summary dashboard={dashboard} />
                <Box>
                    <SectionLabel>{t('myInitiatives')}</SectionLabel>
                    <Initiatives enrollments={enrollments} />
                </Box>
            </Stack>
        </Panel>
    );
}

function SectionLabel({ children }) {
    return (
        <Typography variant="overline" color="text.secondary"
            sx={{ display: "block", fontWeight: 700, letterSpacing: ".08em", lineHeight: 1, mb: 1.5 }}>
            {children}
        </Typography>
    );
}

function initials(user) {
    const parts = [user.firstName, user.lastName].filter(Boolean);
    const source = parts.length ? parts : [user.username || ""];
    return source.map(p => p.trim().charAt(0).toUpperCase()).join("").slice(0, 2);
}

/** Scheda in testa: chi e' l'utente, con telefono, data di nascita e sesso. */
function ProfileCard({ user }) {
    const { t } = useTranslation();
    const fullName = [user.firstName, user.lastName].filter(Boolean).join(" ") || user.username || "";

    const fields = [
        { label: t('phoneNumber'), value: user.phoneNumber },
        { label: t('birthday'), value: user.birthDate && dayjs(new Date(user.birthDate)).format("DD/MM/YYYY") },
        { label: t('gender'), value: user.genderCode != null && t(getGenderCodeValue(user.genderCode)) },
    ];

    return (
        <Box sx={{ bgcolor: GREY_TINT, borderRadius: RADIUS, p: { xs: 2, md: 3 } }}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2.5} alignItems={{ xs: "flex-start", sm: "center" }}>
                <Avatar src={user.avatarUrl || undefined}
                    sx={{ width: 72, height: 72, bgcolor: "#fcd4d4", color: RED, fontWeight: 800, fontSize: 26 }}>
                    {initials(user)}
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                    <Typography variant="h4" sx={{ fontWeight: 800, fontSize: { xs: 26, md: 32 } }}>{fullName}</Typography>
                    <Typography color="text.secondary" sx={{ wordBreak: "break-all" }}>
                        {[user.username && `@${user.username}`, user.email].filter(Boolean).join(" · ")}
                    </Typography>
                    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.75, color: "text.secondary" }}>
                        <PhoneIphoneIcon sx={{ fontSize: 16 }} />
                        <Typography variant="body2">{t('suggestUsingMobileApp')}</Typography>
                    </Stack>
                </Box>
            </Stack>
            <Divider sx={{ my: 2.5, borderColor: "#efeff2" }} />
            <Grid container spacing={2}>
                {fields.map(f =>
                    <Grid item xs={12} sm={4} key={f.label}>
                        <Typography variant="caption" color="text.secondary">{f.label}</Typography>
                        <Typography sx={{ fontWeight: 600, wordBreak: "break-word" }}>{f.value || "-"}</Typography>
                    </Grid>)}
            </Grid>
        </Box>
    );
}

function Summary({ dashboard = {} }) {
    const { t } = useTranslation();
    const decimal = { maximumFractionDigits: 1 };
    const stats = [
        { label: t('sessions'), value: (dashboard.sessionNumber || 0).toLocaleString() },
        { label: t('nationalKm'), value: `${(dashboard.distance || 0).toLocaleString(undefined, decimal)} km` },
        { label: t('nationalPoints'), value: Math.round(dashboard.points || 0).toLocaleString() },
        { label: t('totalCo2'), value: `${(dashboard.co2 || 0).toLocaleString(undefined, decimal)} kg`, green: true },
    ];
    return (
        <Grid container spacing={2}>
            {stats.map(s =>
                <Grid item xs={6} md={3} key={s.label}>
                    <Box sx={{ bgcolor: s.green ? GREEN_TINT : RED_TINT, borderRadius: RADIUS, px: 2.5, py: 2, height: "100%" }}>
                        <Typography sx={{ color: s.green ? GREEN : RED, fontWeight: 800, fontSize: { xs: 22, md: 28 }, lineHeight: 1.15 }}>
                            {s.value}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">{s.label}</Typography>
                    </Box>
                </Grid>)}
        </Grid>
    );
}

function isActive(e) {
    if (e.deletedDate) return false;
    const end = e.endDate_date || e.endDate;
    return !end || dayjs(end).isAfter(dayjs().startOf("day"));
}

/**
 * Le iniziative con i loro totali e il cluster.
 * L'elenco ha un'altezza massima e scorre, cosi' tante iscrizioni non allungano la pagina.
 */
function Initiatives({ enrollments }) {
    const { t } = useTranslation();
    // Il nome arriva con l'iscrizione: non serve l'elenco delle iniziative, che per un pinbiker puo'
    // non contenerle tutte. Prima le attive, poi le terminate.
    const list = enrollments
        .filter(e => e.organizationTitle)
        .map(e => ({ ...e, active: isActive(e) }))
        .sort((a, b) => Number(b.active) - Number(a.active));

    if (list.length === 0)
        return <Typography color="text.secondary">{t('noInitiatives')}</Typography>;

    return (
        <Box sx={{ maxHeight: 520, overflowY: "auto", pr: 0.5 }}>
            <Grid container spacing={1.5}>
                {list.map(e =>
                    <Grid item xs={12} sm={6} lg={4} key={e.id || e.organization}>
                        <Box sx={{ bgcolor: GREY_TINT, borderRadius: RADIUS, p: 2, opacity: e.active ? 1 : 0.6 }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                                <Typography sx={{ fontWeight: 700 }}>{e.organizationTitle}</Typography>
                                <Chip size="small" label={t(e.active ? 'active' : 'notActive')}
                                    sx={e.active
                                        ? { bgcolor: GREEN_TINT, color: GREEN, fontWeight: 600 }
                                        : { bgcolor: "#f0f0f3", color: "text.secondary", fontWeight: 600 }} />
                            </Stack>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                                {(e.urbanKm || 0).toFixed(2)} km · {(e.points || 0).toFixed(0)} {t('points').toLowerCase()} · {(e.euro || 0).toFixed(2)} {t('currency')}
                            </Typography>
                            {e.customFieldName &&
                                <Typography variant="body2" color="text.secondary">
                                    {t('customField')}: <b>{e.customFieldName}</b>
                                </Typography>}
                        </Box>
                    </Grid>)}
            </Grid>
        </Box>
    );
}
